import { EMBEDDING_REASON_LABELS, zonedDateKey, type DocumentVectorsDto, type EmbeddingFailureDto } from '@dbrain/shared';

/**
 * Onay politikaları → Belge araması's status without the DOM (Phase 15g, D-253): what it says when the company's
 * documents have no vectors, from GET /api/tenant/document-vectors. Texts are i18n keys under
 * `policies.documentVectors`; the provider's phrase after the status ("500 · Connection error") stays as the provider
 * says it, in every language.
 */
export const DOCUMENT_VECTORS_KEY = ['tenant', 'document-vectors'] as const;

type T = (key: string, options?: Record<string, unknown>) => string;

/** "500 · Connection error": the provider's status and its phrase, or null. */
export function failureDetail(failure: Pick<EmbeddingFailureDto, 'status' | 'reason'>): string | null {
  const parts = [failure.status ? String(failure.status) : null, failure.reason ? EMBEDDING_REASON_LABELS[failure.reason] : null].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : null;
}

/** A time of the company's day: "10:40" today, "2 Kas 00:10" another day; "birazdan" for a next attempt already due. */
export function attemptTime(iso: string, locale: string, timeZone: string, t: T, now = new Date(), future = false): string {
  const at = new Date(iso);
  if (future && at.getTime() <= now.getTime()) return t('policies.documentVectors.soon');
  const sameDay = zonedDateKey(at, timeZone) === zonedDateKey(now, timeZone);
  return sameDay
    ? at.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', timeZone })
    : at.toLocaleString(locale, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone });
}

export interface VectorsNotice {
  kind: 'paused' | 'documents' | 'indexing';
  title: string;
  lines: string[];
  /** "Şimdi yeniden dene" (once, on the first warning) */
  retry: boolean;
}

/** What the card says; empty while everything works (or the company chose words only). */
export function documentVectorsNotices(data: DocumentVectorsDto, t: T, locale: string, timeZone: string, now = new Date()): VectorsNotice[] {
  if (data.documentSearch !== 'hybrid') return [];
  const when = (iso: string, future = false) => attemptTime(iso, locale, timeZone, t, now, future);
  const problem = (failure: EmbeddingFailureDto) => {
    const detail = failureDetail(failure);
    const words = t(`policies.documentVectors.problems.${failure.problem}`);
    return detail ? t('policies.documentVectors.problem', { problem: words, detail }) : t('policies.documentVectors.problemPlain', { problem: words });
  };
  const notices: VectorsNotice[] = [];
  const { pause, documents } = data;
  if (pause) {
    notices.push({
      kind: 'paused',
      title: t('policies.documentVectors.paused.title'),
      lines: [
        `${problem(pause)} ${t('policies.documentVectors.pausedHint')}${data.waiting > 0 ? ` ${t('policies.documentVectors.waiting', { count: data.waiting })}` : ''}`,
        pause.probing
          ? t('policies.documentVectors.probing', { last: when(pause.lastAttemptAt) })
          : t('policies.documentVectors.attemptsSince', { last: when(pause.lastAttemptAt), next: when(pause.nextAttemptAt, true), count: pause.failures, since: when(pause.since) }),
      ],
      retry: true,
    });
  }
  if (documents) {
    notices.push({
      kind: 'documents',
      title: t('policies.documentVectors.documents.title', { count: documents.count }),
      lines: [`${problem(documents)} ${t('policies.documentVectors.documentsHint')}`, t('policies.documentVectors.attempts', { last: when(documents.lastAttemptAt), next: when(documents.nextAttemptAt, true) })],
      retry: !pause,
    });
  }
  if (!pause && !documents && data.waiting > 0) notices.push({ kind: 'indexing', title: t('policies.documentVectors.indexing', { count: data.waiting }), lines: [], retry: false });
  return notices;
}
